import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  Container,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  DataGridPremium,
  GridColDef,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid-premium';
import ReactJson from 'react-json-view';
import { t } from 'i18next';
import { ApiMonitoringDelta } from '../../../api/hexa/ApiMonitoringDelta';
import { MonitoringDelta } from '../../../api/hexa/models/MonitoringDeltaModels';
import { useDebounce } from 'use-debounce';

const MonitoringList = () => {
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [eaNumber, setEaNumber] = useState<string>('');
  const [debounceEntityId] = useDebounce(eaNumber, 500);
  const [jsonData, setJsonData] = useState<MonitoringDelta | { error: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [row, setRow] = useState<Record<string, any> | undefined>();
  const [rows, setRows] = useState<{ id: string; date: string; diff_type: any }[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState({
    open: false,
    message: '',
  });

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'date',
        headerName: t('monitoring_list.diff_date'),
        flex: 1,
        renderCell: (params) => (
          <Tooltip title={params.value}>
            <Typography noWrap>{params.value}</Typography>
          </Tooltip>
        ),
      },
      {
        field: 'diff_type',
        headerName: t('monitoring_list.diff_type'),
        flex: 1,
        renderCell: (params) => (
          <Tooltip title={params.value}>
            <Typography noWrap>{params.value}</Typography>
          </Tooltip>
        ),
      },
    ],
    []
  );

  const fetchMonitoringDelta = useCallback(async () => {
    if (!debounceEntityId) return;
    setLoading(true);
    try {
      const { total, delta } = await ApiMonitoringDelta.show({
        id: `EA${debounceEntityId}`,
        page: paginationModel.page,
        size: paginationModel.pageSize,
        sort_direction: sortModel[0]?.sort ?? 'desc',
      });

      setRowCount(total);
      setRows(
        Object.entries(delta).map(([date, data]) => ({
          id: date,
          date,
          diff_type: data.diff_type,
        }))
      );
      setJsonData({ delta });
    } catch (error: any) {
      setError({
        open: true,
        message: `Error ${error.status ?? 500} fetching the monitoring delta list: 
      ${error.response?.data.error ?? 'Error fetching the monitoring delta list'}`,
      });
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize, debounceEntityId, sortModel]);

  useEffect(() => {
    if (debounceEntityId) {
      fetchMonitoringDelta();
    }
  }, [debounceEntityId, fetchMonitoringDelta]);

  const handleClickOpen = (date: string) => {
    if (jsonData && 'delta' in jsonData) {
      const rowData = jsonData.delta[date]?.diff;
      setRow(rowData ? rowData : undefined);
      setSelectedDate(date);
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setRow(undefined);
    setSelectedDate(null);
  };

  return (
    <Container maxWidth="sm">
      <Card sx={{ p: 2, mt: 4, textAlign: 'center' }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
          <TextField
            value={eaNumber}
            onChange={(e) => setEaNumber(e.target.value)}
            variant="outlined"
            size="small"
            placeholder="123456"
            type="number"
            InputProps={{
              startAdornment: <InputAdornment position="start">EA</InputAdornment>,
            }}
            sx={{ backgroundColor: 'white', flexGrow: 1 }}
          />
          {loading && <CircularProgress size={20} />}
        </Stack>
      </Card>

      <Card sx={{ p: 2, mt: 2 }}>
        <DataGridPremium
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          onRowClick={(params) => handleClickOpen(params.row.date)}
          loading={loading}
          paginationModel={paginationModel}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={rowCount}
          paginationMode="server"
          pageSizeOptions={[10, 25, 50, 100]}
          autoHeight={true}
          density="compact"
          pagination={true}
        />
      </Card>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">
          {selectedDate ? `${selectedDate}` : ''}
        </Typography>
          <div>
            <Tooltip title={copied ? t('monitoring_list.copied') : t('monitoring_list.copy')}>
              <IconButton
                aria-label="copy"
                onClick={() => {
                  if (row) {
                    navigator.clipboard.writeText(JSON.stringify(row, null, 2));
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  }
                }}
                sx={{ mr: 1 }}
              >
                📋
              </IconButton>
            </Tooltip>

            <IconButton aria-label="close" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent dividers>
          {row && (
            <ReactJson
              src={row}
              name={false}
              theme="monokai"
              collapsed={false}
              displayDataTypes={false}
              displayObjectSize={false}
              enableClipboard={false}
              style={{ fontSize: '14px' }}
              collapseStringsAfterLength={false}
            />          
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default MonitoringList;
